import {Component, ElementRef, inject, ViewChild} from '@angular/core';
import {MatButtonModule} from "@angular/material/button";
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {ResearchPaperCreateDTO, ResearchPaperDTO} from '../../dto/research-paper';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatInputModule} from '@angular/material/input';
import {Store} from '@ngrx/store';
import {
  createResearchBulkPaper,
  createResearchPaper,
  updateResearchPaper
} from '../../services/research-paper/research-paper.actions';
import {doiValidator, yearValidator} from '../../../../utils/base/validators';
import {MatTabsModule} from '@angular/material/tabs';
import {CloseableDialogComponent} from "../../../../utils/components/closeable-dialog/closeable-dialog.component";

@Component({
  selector: 'app-research-paper-update-dialog',
    imports: [
        MatDialogModule,
        MatInputModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        ReactiveFormsModule,
        MatTabsModule,
        CloseableDialogComponent
    ],
  templateUrl: './research-paper-update-dialog.component.html',
  styleUrl: './research-paper-update-dialog.component.scss'
})
export class ResearchPaperUpdateDialogComponent {
  private readonly store: Store = inject(Store);
  private readonly dialogRef: MatDialogRef<ResearchPaperUpdateDialogComponent> = inject(MatDialogRef);
  private readonly fb: FormBuilder = inject(FormBuilder);
  readonly paper: ResearchPaperDTO = inject<ResearchPaperDTO>(MAT_DIALOG_DATA);

  @ViewChild('fileInput') fileInput!: ElementRef;
  uploadedFile?: File;

  @ViewChild('csvFileInput') csvFileInput!: ElementRef;
  uploadedCSVFile?: File;
  @ViewChild('filesInput') filesInput!: ElementRef;
  uploadedPDFFiles?: File[];

  researchPaperForm: FormGroup = this.fb.group({
    title: [this.paper?.title || '', Validators.required],
    abstractText: [this.paper?.abstractText || '', Validators.required],
    venue: [this.paper?.venue || '', Validators.required],
    referenceCount: [this.paper?.referenceCount, Validators.min(0)],
    citationCount: [this.paper?.citationCount, Validators.min(0)],
    isOpenAccess: [this.paper?.isOpenAccess || false],
    authors: [this.paper?.authors || '', Validators.required],
    year: [this.paper?.year || '', [Validators.required, yearValidator()]],
    url: [this.paper?.url || '', Validators.pattern('https?://.+')],
    pdfUrl: [this.paper?.pdfUrl || '', Validators.pattern('https?://.+')],
    semanticScholarId: [this.paper?.semanticScholarId || ''],
    doi: [this.paper?.doi || '', [Validators.required, doiValidator()]],
    corpusId: [this.paper?.corpusId || ''],
    magId: [this.paper?.magId || ''],
    pubMedId: [this.paper?.pubMedId || ''],
    file: [null],
  });

  selectedTabIndex = 0;

  save(): void {
    if (this.selectedTabIndex == 0) {
      if (this.researchPaperForm.valid) {
        if (this.isUpdate()) {
          this.update();
        } else {
          this.createSingle();
        }
        this.dialogRef.close(this.researchPaperForm.value);

      }
    } else {
      this.createBulk();
      this.dialogRef.close();
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  isUpdate(): boolean {
    return !!(this.paper && this.paper.id);
  }

  private update(): void {
    const updatedPaper: ResearchPaperDTO = {...this.paper, ...this.researchPaperForm.value};
    this.store.dispatch(() => updateResearchPaper({
      paper: updatedPaper,
      projectId: this.paper.projectId,
      id: this.paper.id
    }));
  }

  onDrop(event: any) {
    event.preventDefault();
    if (event.dataTransfer.files.length > 0) {
      this.uploadedFile = event.dataTransfer.files[0];
      this.researchPaperForm.patchValue({file: this.uploadedFile});
    }
  }

  onDragOver(event: any) {
    event.preventDefault();
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.uploadedFile = input.files[0];
      this.researchPaperForm.patchValue({file: this.uploadedFile});
    }
  }


  onDropCSV(event: any) {
    event.preventDefault();
    if (event.dataTransfer.files.length > 0) {
      this.uploadedCSVFile = event.dataTransfer.files[0];
    }
  }

  onDropFiles(event: any) {
    event.preventDefault();
    if (event.dataTransfer.files.length > 0) {
      this.uploadedPDFFiles = event.dataTransfer.files;
    }
  }

  triggerCSVFileInput() {
    this.csvFileInput.nativeElement.click();
  }

  triggerFilesInput() {
    this.filesInput.nativeElement.click();
  }

  onCSVFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.uploadedCSVFile = input.files[0];
    }
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      Array.from(input.files).forEach((pdf) => {
        if (!this.uploadedPDFFiles) {
          this.uploadedPDFFiles = [];
        }
        this.uploadedPDFFiles.push(pdf);
      });
    }
  }

  removePDFFile(index: number): void {
    if (this.uploadedPDFFiles) {
      this.uploadedPDFFiles = this.uploadedPDFFiles.filter((_, i) => i !== index);
    }
  }

  formIsValid(): boolean {
    if (this.selectedTabIndex == 0) {
      return !this.researchPaperForm.valid;
    }
    return this.uploadedCSVFile != null;
  }


  private createSingle(): void {
    const createdPaper: ResearchPaperCreateDTO = {...this.paper, ...this.researchPaperForm.value};

    this.store.dispatch(() => createResearchPaper({
      projectId: this.paper.projectId,
      file: this.uploadedFile,
      paper: createdPaper
    }));
  }

  private createBulk(): void {
    if (!this.uploadedCSVFile) {
      return;
    }
    const files: File[] = [];
    if (this.uploadedPDFFiles) {
      Array.from(this.uploadedPDFFiles).forEach((pdf) => {
        files.push(pdf);
      });
    }
    this.store.dispatch(() => createResearchBulkPaper({
      projectId: this.paper.projectId,
      csv: this.uploadedCSVFile!,
      pdfs: files
    }));
  }

  toggleMaximize(isMaximized: boolean): void {
    if (isMaximized) {
      this.dialogRef.updateSize('100vw', '100vh');
    } else {
      this.dialogRef.updateSize("600px", "80vh");
    }
  }
}
